import { useEffect, useRef, useState } from 'react';
import { Animated, Easing, NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { TextInput } from 'react-native-gesture-handler';
import {
  addQueryParams,
  controlAPICall,
  getSecureValue,
  isWeb,
  LEVELS,
  removeQueryParam,
  searchDisplayMode,
  setSecureValue,
} from '../../../../../../../helpers';
import { BATCH_LIST_SELECT, TABLE_NAMES } from '@v2/pages/common/constants/base.constants';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { useHandleNavigationMachinesHeader } from '@v2/pages/machines/hooks/useHandleNavigationMachineHeader';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';
import { useTableTools } from '@v2/pages/common/hooks/useTableTools';
import { DisplayMode } from '@v2/pages/common/components/ContextHeader/ContextHeader';
import { OwnProps, DispatchProps, FetchBatchesParams } from '@v2/pages/common/components/BatchListContent/types';
import { getDisplayModeFlags, getMarginTop, getODataBySelectedGroup, isCloseToBottom, getTopValue } from '@v2/pages/common/components/BatchListContent/helpers';

export interface UseManageBatchListContentParams extends OwnProps, DispatchProps {
  allowLoadMoreData: boolean;
  setLoadDataDisabled: (v: boolean) => void;
  displayScrollViewLoader: boolean;
  setDisplayScrollViewLoader: (v: boolean) => void;
  selectedItem: any;
  batchList: any[];
  setBatchList: (v: any[]) => void;
  productionBatchesSearch: (params: any) => Promise<any>;
  batchListWidth: number;
  setBatchListWidth: (v: number) => void;
  productionBatchesSuggestion: (params: any) => Promise<any>;
  setBatchListContainerHeight: (v: number) => void;
  setBatchListYPosition: (v: number) => void;
  loadDataDisabled: boolean;
  setAllowLoadMoreData: (v: boolean) => void;
}

export const useManageBatchListContent = (params: UseManageBatchListContentParams) => {
  const {
    noMachine,
    getProductionBatchByNCFile,
    getProductionBatchByMachineId,
    ncFileId,
    allowLoadMoreData,
    setLoadDataDisabled,
    displayScrollViewLoader,
    setDisplayScrollViewLoader,
    selectedItem,
    batchList,
    setBatchList,
    productionBatchesSearch,
    productionBatchesSuggestion,
    setBatchListContainerHeight,
    setBatchListYPosition,
    loadDataDisabled,
    setAllowLoadMoreData,
    batchListContainerHeight,
    fetchBatchList,
    idForGettingBatchList,
    selectedGroup,
  } = params;

  const { isMachinesScreen } = useHandleNavigationMachinesHeader();
  const { IS_NARROW_WEB } = useDeviceSize();

  const urlParams = new URLSearchParams(isWeb ? window?.location?.search : ({} as any));
  const initialSearchValue = urlParams.get('search');

  const [batchLoading, setBatchLoading] = useState(true);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState<string>(initialSearchValue ?? '');
  const [suggestions, setSuggestionsLocal] = useState<any[]>([]);
  const [facets, setFacets] = useState<any[]>([]);
  const [isError, setIsError] = useState<boolean>(false);
  const [displayMode, setDisplayModeInternal] = useState<DisplayMode | null>(null);
  const [contentHeight, setContentHeight] = useState<number>(0);
  const [searchWasTriggered, setSearchWasTriggered] = useState<boolean>(false);
  const [searchTileName, setSearchTileName] = useState<string>('');
  const [statisticTileName, setStatisticTileName] = useState<string>('');

  const {
    columnVisibilityModel,
    setColumnVisibilityModel,
    filter,
    setFilter,
    filterModel,
    setFilterModel,
    orderBy,
    setOrderBy,
    sortModel,
    setSortModel,
    oDataFilter,
    setODataFilter,
    usedODataFilter,
    customAnchorRef,
    apiRef,
  } = useTableTools(TABLE_NAMES.batchList);

  const abortControllerSuggestion = useRef<AbortController | null>(null);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const searchRef = useRef<TextInput | null>(null);

  const top = getTopValue(displayMode, IS_NARROW_WEB);

  const batchListOffset = useRef<number>(0);
  const batchListInfoTileModeOffset = useRef<number>(0);

  const { isAllSelected } = useHandleParams();

  const machineId = selectedItem.machineId;

  const { isSmallMode, isBlockMode, isLineMode, isTileMode, isTableMode } = getDisplayModeFlags(displayMode);

  const oDataBySelectedGroup = getODataBySelectedGroup(selectedGroup);

  const fetchBatches = async ({ listOffset, oDataFilter, searchText, orderBy: _orderBy }: FetchBatchesParams) => {
    const filters = { top, skip: listOffset, orderBy: _orderBy ?? orderBy };
    const searchQuery = searchText ?? searchValue;
    const orderByFilter = _orderBy ?? orderBy ?? ['EndedOn desc'];
    const batchFacets = ['LinkStatus', 'NcFileStatus'];

    if (fetchBatchList && idForGettingBatchList) {
      return await fetchBatchList({ id: idForGettingBatchList, filters, select: BATCH_LIST_SELECT, search: searchQuery, facets: batchFacets, oDataFilter });
    }

    if (noMachine && !ncFileId) {
      return undefined;
    }

    if (ncFileId) {
      return await getProductionBatchByNCFile({ id: ncFileId, filters, select: BATCH_LIST_SELECT, search: searchQuery, facets: batchFacets, oDataFilter });
    }

    if (isAllSelected) {
      return await productionBatchesSearch({
        data: { top, skip: listOffset },
        filter: {
          orderBy: orderByFilter,
          search: searchQuery,
          oDataFilter: oDataBySelectedGroup ? `(${oDataBySelectedGroup})` + (oDataFilter ? ' and ' + oDataFilter : '') : oDataFilter,
        },
        select: BATCH_LIST_SELECT,
        resetHistory: true,
        ignoreHistory: true,
      });
    }

    if (machineId) {
      return await getProductionBatchByMachineId({
        machineId,
        filters,
        select: BATCH_LIST_SELECT,
        search: searchQuery,
        facets: batchFacets,
        oDataFilter,
      });
    }

    return undefined;
  };

  const loadBatchList = async (offset?: number, searchText?: string, filters?: { orderBy: string[] }) => {
    if (isError) {
      setIsError(false);
    }

    if (isTileMode && batchList.length && batchListInfoTileModeOffset.current + top < batchList.length && offset == null) {
      if (!!batchList.length) {
        setDisplayScrollViewLoader(true);
      }
      batchListInfoTileModeOffset.current += top;
      batchListOffset.current += top;
      setTimeout(() => {
        setDisplayScrollViewLoader(false);
      }, 500);
      return;
    }

    const listOffset = offset ?? batchListOffset.current;

    if (totalCount && totalCount <= listOffset) return;

    if (!listOffset) {
      setBatchLoading(true);
    }

    if (setDisplayScrollViewLoader && !!batchList.length) {
      setDisplayScrollViewLoader(!batchLoading && listOffset > 0);
    }

    try {
      const batches = await fetchBatches({
        listOffset,
        oDataFilter: usedODataFilter.current,
        searchText,
        ...(filters?.orderBy ? { orderBy: filters.orderBy } : {}),
      });

      if (batches && batches.searchResult) {
        setTotalCount(batches.totalResultCount);
        setFacets(batches.facets);
        setBatchList(listOffset === 0 ? batches.searchResult : [...batchList, ...batches.searchResult]);
        batchListOffset.current += top;
        batchListInfoTileModeOffset.current += top;
      }

      if (!listOffset) {
        setBatchLoading(false);
        if (setLoadDataDisabled) {
          setLoadDataDisabled(false);
        }
      }

      if (setDisplayScrollViewLoader) {
        setDisplayScrollViewLoader(false);
      }
    } catch (e) {
      const err = e as Error;
      setIsError(true);
      console.error(err.message);
    }
  };

  // Effects
  useEffect(() => {
    if (displayMode === null) return;
    if (setLoadDataDisabled) {
      setLoadDataDisabled(true);
      batchListOffset.current = 0;
    }
    setBatchList([]);
    setBatchListYPosition(0);
    setTotalCount(null);
    batchListInfoTileModeOffset.current = 0;
    loadBatchList(0);
  }, [selectedItem.machineStatus?.productionBatchId, selectedItem?.id, !!displayMode, selectedGroup?.machineGroupId]);

  useEffect(() => {
    if (!batchListOffset.current || !allowLoadMoreData || displayScrollViewLoader) return;
    loadBatchList();
  }, [allowLoadMoreData]);

  useEffect(() => {
    (async () => {
      const initialDisplayMode = getSecureValue(`${LEVELS.ProductionBatches}-displayMode`);
      setDisplayMode(initialDisplayMode || searchDisplayMode.infoTiles);
    })();
  }, []);

  useEffect(() => {
    if (searchValue) {
      addQueryParams({ search: searchValue });
    } else {
      removeQueryParam('search');
    }
  }, [searchValue]);

  useEffect(() => {
    if (contentHeight && batchListContainerHeight && contentHeight <= batchListContainerHeight) {
      loadBatchList();
    }
  }, [contentHeight, batchListContainerHeight]);

  // Actions
  const setDisplayMode = async (mode: DisplayMode) => {
    setSecureValue(`${LEVELS.ProductionBatches}-displayMode`, mode);
    setDisplayModeInternal(mode);
    batchListInfoTileModeOffset.current = 0;
  };

  const setSuggestions = (s: any) => {
    if (abortControllerSuggestion.current) {
      abortControllerSuggestion.current.abort();
    }
    setSuggestionsLocal(s);
  };

  const onSearch = (v: string) => {
    setSearchValue(v);
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (!v) {
      setSuggestions([]);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      const d = {
        searchText: v,
        useFuzzyMatching: false,
        size: top,
        highlight: {
          preTag: '<p>',
          postTag: '<p>',
        },
      };
      const result = await controlAPICall(productionBatchesSuggestion, d, abortControllerSuggestion);
      setSuggestions(result || []);
    }, 300);
  };

  const cancelAnimation = async () => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    setSearchValue('');
    setSuggestions([]);
    setBatchList([]);
    setSearchWasTriggered(false);
    batchListOffset.current = 0;
    setTotalCount(null);
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 300,
      easing: Easing.ease,
      useNativeDriver: false,
    }).start();
    await loadBatchList(0, '');
  };

  const submit = async (v: string) => {
    setSuggestions([]);

    const text = v && typeof v === 'string' ? v : searchValue;

    setSearchValue(text);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    setSearchWasTriggered(true);
    setBatchList([]);
    batchListOffset.current = 0;
    batchListInfoTileModeOffset.current = 0;
    await loadBatchList();
  };

  const searchBatchesWithTile = ({
    oDataFilter,
    name,
  }: {
    level: (typeof LEVELS)[keyof typeof LEVELS];
    oDataFilter: string;
    resetHistory: boolean;
    name: string;
  }) => {
    batchListOffset.current = 0;
    batchListInfoTileModeOffset.current = 0;
    usedODataFilter.current = usedODataFilter.current === oDataFilter ? '' : oDataFilter;
    setSearchWasTriggered(true);
    setSearchTileName(name);
    loadBatchList(0);
  };

  let scrollTimeoutId: NodeJS.Timeout | null = null;

  const scrollListener = ({ nativeEvent }: NativeSyntheticEvent<NativeScrollEvent>) => {
    const {
      contentOffset: { y },
    } = nativeEvent;

    if (scrollTimeoutId) {
      clearTimeout(scrollTimeoutId);
    }

    scrollTimeoutId = setTimeout(() => {
      setBatchListYPosition(y);
      scrollTimeoutId = null;
    }, 100);

    if (isCloseToBottom(nativeEvent) && !allowLoadMoreData && !loadDataDisabled) {
      setAllowLoadMoreData(true);
      setTimeout(() => setAllowLoadMoreData(false), 1000);
    }
  };

  const marginTop = getMarginTop(displayMode, IS_NARROW_WEB);
  const scrollViewNeeded = !isTableMode;

  return {
    // State
    batchLoading,
    totalCount,
    searchValue,
    suggestions,
    facets,
    isError,
    displayMode,
    contentHeight,
    searchWasTriggered,
    searchTileName,
    statisticTileName,
    setStatisticTileName,
    setContentHeight,

    // Table tools
    columnVisibilityModel,
    setColumnVisibilityModel,
    filter,
    setFilter,
    filterModel,
    setFilterModel,
    orderBy,
    setOrderBy,
    sortModel,
    setSortModel,
    oDataFilter,
    setODataFilter,
    usedODataFilter,
    customAnchorRef,
    apiRef,

    // Refs
    fadeAnim,
    searchRef,
    batchListOffset,
    batchListInfoTileModeOffset,

    // Derived
    isMachinesScreen,
    IS_NARROW_WEB,
    top,
    isSmallMode,
    isLineMode,
    isTileMode,
    isTableMode,
    marginTop,
    scrollViewNeeded,

    // Actions
    setDisplayMode,
    setSuggestions,
    onSearch,
    cancelAnimation,
    submit,
    searchBatchesWithTile,
    scrollListener,
    loadBatchList,
    setBatchListContainerHeight,
  };
};
